import React, { useState } from "react";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  useGetNetplusClubReportQuery,
  useGetNetplusTeamsQuery,
} from "../../../store/appApi";
import { downloadNetplusReportApi } from "../../../services/netplusTeam";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../store/authSlice";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const inputClass =
  "rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2026-11-01" -> "01 Nov 2026"
const fmtDay = (k) => `${k.slice(8, 10)} ${MONTHS[Number(k.slice(5, 7)) - 1]} ${k.slice(0, 4)}`;

// Back-dated report: the day it was really submitted, under its date.
const LateNote = ({ days }) =>
  days?.length ? (
    <div className="text-[11px] font-semibold text-amber-700">
      Back-dated · submitted on {days.map(fmtDay).join(", ")}
    </div>
  ) : null;

// Daily figure with the running total next to it, e.g. "10  (Σ 15)".
const MGR_COLS = [
  { key: "pendingCases", label: "Total Pending Cases", cumulative: false },
  { key: "todayVisits", label: "Today Visits", cumulative: true },
  { key: "modemRecovered", label: "Modem Recovered", cumulative: true },
  { key: "recharged", label: "Recharged", cumulative: true },
  { key: "untraceable", label: "Untraceable", cumulative: true },
  { key: "balance", label: "Balance", cumulative: false },
];

const Kpi = ({ label, value, sub }) => (
  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
      {value ?? 0}
    </div>
    {sub ? <div className="text-[11px] text-gray-400">{sub}</div> : null}
  </div>
);

const NetplusReports = () => {
  const isSuperAdmin = useSelector(selectUserRole) === "super-admin";
  const [teamId, setTeamId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [downloading, setDownloading] = useState(false);

  const { data: teamData } = useGetNetplusTeamsQuery({ active: "true" });
  const { data, isFetching } = useGetNetplusClubReportQuery({ teamId, from, to });
  const teams = data?.teams || [];
  const missing = data?.notSubmitted || [];
  const isToday = data?.notSubmittedDate === data?.today;

  const download = async () => {
    try {
      setDownloading(true);
      const res = await downloadNetplusReportApi({ teamId, from, to });
      saveAs(
        new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `netplus-reports-${data?.from || ""}-to-${data?.to || ""}.xlsx`,
      );
    } catch (err) {
      Swal.fire({ icon: "error", title: "Download failed", text: "Please try again." });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Netplus Reports" motherMenu="Workforce" />
      <div className="space-y-6">
        <section className={`${panel} p-6`}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Netplus Reports</h1>
              <p className="mt-1 text-sm text-gray-500">
                What the team submitted in Staff Reporting, plus the manager's own
                daily sheet.
              </p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Team
                <select
                  className={`${inputClass} mt-1 block`}
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                >
                  <option value="">{isSuperAdmin ? "All teams" : "All my teams"}</option>
                  {(teamData?.teams || []).map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                From
                <input
                  type="date"
                  className={`${inputClass} mt-1 block`}
                  value={from || data?.from || ""}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </label>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                To
                <input
                  type="date"
                  className={`${inputClass} mt-1 block`}
                  value={to || data?.to || ""}
                  onChange={(e) => setTo(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
                disabled={!teams.length || downloading}
                onClick={download}
              >
                {downloading ? "Preparing…" : "Download Excel"}
              </button>
            </div>
          </div>
        </section>

        {teams.length > 0 && (
          <section
            className={`rounded-3xl border p-5 ${
              missing.length
                ? "border-red-200 bg-red-50"
                : "border-emerald-200 bg-emerald-50"
            }`}
          >
            {missing.length ? (
              <>
                <div className="text-sm font-semibold text-red-700">
                  {isToday ? "Today's" : `${data.notSubmittedDate}`} report not submitted
                  by ({missing.length})
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {missing.map((n) => (
                    <span
                      key={n.employeeId}
                      className="rounded-full bg-white px-3 py-1 text-xs font-medium text-red-700 shadow-sm"
                      title={n.teams.join(", ")}
                    >
                      {n.name}
                      {n.role === "Manager Netplus" ? " (Manager)" : ""}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-sm font-semibold text-emerald-700">
                Everyone has reported for {data.notSubmittedDate}.
              </div>
            )}
          </section>
        )}

        {isFetching && !teams.length && (
          <section className={`${panel} p-10 text-center text-gray-400`}>Loading…</section>
        )}
        {!isFetching && !teams.length && (
          <section className={`${panel} p-10 text-center text-gray-400`}>
            No team found.
          </section>
        )}

        {teams.map((t) => (
          <section key={t._id} className="space-y-3">
            <div className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              {t.name} — Manager: {t.manager?.name || "—"}
            </div>

            {/* Manager's own daily sheet (staff reporting form) */}
            <div className={panel}>
              <div className="border-b border-gray-100 px-5 py-3 text-sm font-semibold text-gray-700">
                Manager daily report
              </div>
              {t.managerTotals && (
                <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-6">
                  <Kpi label="Pending cases" value={t.managerTotals.pendingCases} sub="latest" />
                  <Kpi label="Visits" value={t.managerTotals.todayVisits} sub="total" />
                  <Kpi label="Recovered" value={t.managerTotals.modemRecovered} sub="total" />
                  <Kpi label="Recharged" value={t.managerTotals.recharged} sub="total" />
                  <Kpi label="Untraceable" value={t.managerTotals.untraceable} sub="total" />
                  <Kpi label="Balance" value={t.managerTotals.balance} sub="latest" />
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Date</th>
                      {MGR_COLS.map((c) => (
                        <th key={c.key} className="px-4 py-2 text-center">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {t.managerDaily.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                          The manager has not added a daily report in this period.
                        </td>
                      </tr>
                    )}
                    {[...t.managerDaily].reverse().map((r) => (
                      <tr key={r.date}>
                        <td className="px-4 py-2">
                          {r.date}
                          <LateNote days={r.submittedOn} />
                        </td>
                        {MGR_COLS.map((c) => (
                          <td key={c.key} className="px-4 py-2 text-center tabular-nums">
                            {r[c.key]}
                            {c.cumulative && (
                              <span className="ml-1 text-xs text-gray-400">
                                (Σ {r.cumulative[c.key]})
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Members: staff report status for the end date */}
            <div className={panel}>
              <div className="border-b border-gray-100 px-5 py-3 text-sm font-semibold text-gray-700">
                Team members
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Member</th>
                      <th className="px-4 py-2 text-center">Days reported</th>
                      <th className="px-4 py-2 text-center">{data.to}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {t.members.map((m) => (
                      <tr key={m.employeeId}>
                        <td className="px-4 py-2 font-medium text-gray-800">
                          {m.name}
                          {m.isManager && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                              MANAGER
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center tabular-nums">{m.daysReported}</td>
                        <td className="px-4 py-2 text-center">
                          {m.submittedOnTo ? (
                            <span className="text-emerald-600">Submitted</span>
                          ) : (
                            <span className="font-semibold text-red-600">Not submitted</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* What each person actually reported in Staff Reporting, per day */}
            <div className={panel}>
              <div className="border-b border-gray-100 px-5 py-3">
                <div className="text-sm font-semibold text-gray-700">
                  Staff reporting — daily
                </div>
                <div className="text-xs text-gray-400">
                  Taken from each member's Staff Reporting sheet.
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Member</th>
                      <th className="px-4 py-2 text-left">Form</th>
                      <th className="px-4 py-2 text-left">Reported</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {t.staffDaily.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                          No staff reports were filed in this period.
                        </td>
                      </tr>
                    )}
                    {t.staffDaily.map((r) => (
                      <tr key={`${r.employeeId}-${r.date}`} className="align-top">
                        <td className="whitespace-nowrap px-4 py-2">
                          {r.date}
                          <LateNote days={r.submittedOn} />
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 font-medium text-gray-800">
                          {r.name}
                        </td>
                        <td className="px-4 py-2 text-gray-500">{r.forms.join(", ")}</td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1.5">
                            {r.items.map((i, idx) => (
                              <span
                                key={`${i.label}-${idx}`}
                                className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700"
                              >
                                {i.label}: <b>{i.value}</b>
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ))}
      </div>
    </>
  );
};

export default NetplusReports;
